/**
 * features/suppliers/SuppliersPage.tsx
 */

import { useState } from 'react';
import { Pencil, Plus, Search, Trash2, Truck } from 'lucide-react';
import { formatTaxId } from '@ft/core';
import { useDebounced } from '~/lib/use-debounced';
import { ApiError } from '~/lib/api';
import type { Party } from '~/lib/types';
import { PageHeader, Pagination, Toolbar } from '~/ui/layout';
import { Button, Card, EmptyState, Input, TableSkeleton } from '~/ui/primitives';
import { ConfirmDialog, useToast } from '~/ui/overlays';
import { useDeleteSupplier, useSuppliers } from './hooks';
import { SupplierFormDialog } from './SupplierFormDialog';

const PAGE_SIZE = 15;

export function SuppliersPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounced(searchInput);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Party | null>(null);
  const [deleting, setDeleting] = useState<Party | null>(null);

  const { data, isLoading, isFetching } = useSuppliers({ page, pageSize: PAGE_SIZE, search });
  const deleteSupplier = useDeleteSupplier();
  const toast = useToast();

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteSupplier.mutateAsync(deleting.id);
      toast.success('ลบผู้ขายแล้ว');
      setDeleting(null);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'ลบไม่สำเร็จ');
    }
  }

  return (
    <div>
      <PageHeader
        title="ผู้ขาย/คู่ค้า"
        description="ข้อมูลผู้ขายที่ใช้ในการบันทึกค่าใช้จ่าย"
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Plus size={16} />
            เพิ่มผู้ขาย
          </Button>
        }
      />

      <Card>
        <Toolbar>
          <div className="relative w-full max-w-xs">
            <Search
              size={15}
              className="absolute top-1/2 left-2.5 -translate-y-1/2 text-fg-subtle"
              aria-hidden
            />
            <Input
              value={searchInput}
              onChange={(event) => {
                setSearchInput(event.target.value);
                setPage(1);
              }}
              placeholder="ค้นหาชื่อ, เลขภาษี, เบอร์โทร"
              className="pl-8"
            />
          </div>
          {isFetching && !isLoading ? <span className="text-xs text-fg-subtle">กำลังอัปเดต…</span> : null}
        </Toolbar>

        {isLoading ? (
          <TableSkeleton columns={4} />
        ) : !data || data.data.length === 0 ? (
          <EmptyState
            icon={<Truck size={28} />}
            title={search ? 'ไม่พบผู้ขายที่ค้นหา' : 'ยังไม่มีข้อมูลผู้ขาย'}
            description={search ? undefined : 'เพิ่มผู้ขายเพื่อใช้ในการบันทึกค่าใช้จ่าย'}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>ชื่อผู้ขาย</th>
                    <th>เลขผู้เสียภาษี</th>
                    <th>โทรศัพท์</th>
                    <th className="w-20" />
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((supplier) => (
                    <tr key={supplier.id}>
                      <td>
                        <p className="font-medium text-fg">{supplier.name}</p>
                        {supplier.email ? (
                          <p className="text-xs text-fg-muted">{supplier.email}</p>
                        ) : null}
                      </td>
                      <td className="tabular text-fg-muted">
                        {supplier.taxId ? formatTaxId(supplier.taxId) : '-'}
                      </td>
                      <td className="text-fg-muted">{supplier.phone || '-'}</td>
                      <td>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            aria-label="แก้ไข"
                            onClick={() => {
                              setEditing(supplier);
                              setFormOpen(true);
                            }}
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            aria-label="ลบ"
                            onClick={() => setDeleting(supplier)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              pageSize={data.pageSize}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <SupplierFormDialog open={formOpen} onClose={() => setFormOpen(false)} supplier={editing} />

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => void handleDelete()}
        title="ลบผู้ขาย"
        message={`ต้องการลบ "${deleting?.name}" ใช่หรือไม่ การลบไม่สามารถย้อนกลับได้`}
        confirmLabel="ลบ"
        loading={deleteSupplier.isPending}
      />
    </div>
  );
}
